import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/admin/layout/Layout';
import Button from '../components/shared/Button';
import Input from '../components/shared/Input';
import Modal from '../components/shared/Modal';
import { adminApi } from '../api/admin';
import type { ApiError } from '../api/client';
import { resolveBackendAssetUrl } from '../utils/assetUrl';
import type { FloorPlan, Project, TeleportPoint } from '../types';

type PointFormState = {
  name: string;
  xPercent: string;
  yPercent: string;
  worldX: string;
  worldY: string;
  worldZ: string;
  worldYaw: string;
};

const emptyPointForm: PointFormState = {
  name: '',
  xPercent: '',
  yPercent: '',
  worldX: '',
  worldY: '',
  worldZ: '',
  worldYaw: '',
};

const ProjectFloorPlan: React.FC = () => {
  const { projectId } = useParams<'projectId'>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [floorPlans, setFloorPlans] = useState<FloorPlan[]>([]);
  const [selectedFloorPlanId, setSelectedFloorPlanId] = useState<string | null>(null);
  const [isCreatingFloor, setIsCreatingFloor] = useState(false);
  const [deletingFloor, setDeletingFloor] = useState(false);
  const [points, setPoints] = useState<TeleportPoint[]>([]);

  const [floorNameInput, setFloorNameInput] = useState('');
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingImage, setSavingImage] = useState(false);

  const [activeMovePointId, setActiveMovePointId] = useState<string | null>(null);

  const [editingPoint, setEditingPoint] = useState<TeleportPoint | null>(null);
  const [pointForm, setPointForm] = useState<PointFormState>(emptyPointForm);
  const [pointModalOpen, setPointModalOpen] = useState(false);
  const [pointSaving, setPointSaving] = useState(false);
  const [pointIconFile, setPointIconFile] = useState<File | null>(null);

  const imageRef = useRef<HTMLImageElement | null>(null);
  const selectedFloorPlanIdRef = useRef<string | null>(null);

  const selectedFloorPlan = useMemo(
    () => floorPlans.find((fp) => fp.id === selectedFloorPlanId) ?? null,
    [floorPlans, selectedFloorPlanId]
  );

  const selectedFloorIndex = useMemo(
    () => floorPlans.findIndex((fp) => fp.id === selectedFloorPlanId),
    [floorPlans, selectedFloorPlanId]
  );

  useEffect(() => {
    selectedFloorPlanIdRef.current = selectedFloorPlanId;
  }, [selectedFloorPlanId]);

  useEffect(() => {
    document.title = 'Project Floor Plan';
    return () => {
      document.title = 'VR Companion Admin';
    };
  }, []);

  const reloadFloorPlans = useCallback(
    async (preferredFloorId?: string | null) => {
      if (!projectId) return null;
      const listResp = await adminApi.floorPlans.listByProject(projectId);
      const list = Array.isArray(listResp) ? listResp : [];
      setFloorPlans(list);

      const hasPreferred =
        typeof preferredFloorId === 'string' &&
        list.some((floor) => floor.id === preferredFloorId);
      const hasCurrent =
        selectedFloorPlanIdRef.current != null &&
        list.some((floor) => floor.id === selectedFloorPlanIdRef.current);
      const nextSelectedId = hasPreferred
        ? (preferredFloorId as string)
        : hasCurrent
          ? (selectedFloorPlanIdRef.current as string)
          : (list[0]?.id ?? null);

      setSelectedFloorPlanId(nextSelectedId);
      return nextSelectedId;
    },
    [projectId]
  );

  const loadData = useCallback(async () => {
    if (!projectId) {
      setError('Project ID is missing from URL');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const projectsResp = await adminApi.projects.list();
      const projects = Array.isArray(projectsResp) ? projectsResp : [];
      setProject(projects.find((p) => p.id === projectId) ?? null);

      const firstFloorId = await reloadFloorPlans();
      if (!firstFloorId) {
        setIsCreatingFloor(true);
        setFloorNameInput('Floor 1');
        setImageUrlInput('');
        setPoints([]);
      }
    } catch (err) {
      setError((err as ApiError).message || 'Failed to load floor plan');
    } finally {
      setLoading(false);
    }
  }, [projectId, reloadFloorPlans]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (isCreatingFloor) return;
    if (!selectedFloorPlan) {
      setFloorNameInput('');
      setImageUrlInput('');
      return;
    }

    setFloorNameInput(selectedFloorPlan.name);
    setImageUrlInput(selectedFloorPlan.imageUrl);
  }, [selectedFloorPlan, isCreatingFloor]);

  useEffect(() => {
    const loadPoints = async () => {
      if (!projectId || !selectedFloorPlanId || isCreatingFloor) {
        setPoints([]);
        return;
      }

      try {
        const pts = await adminApi.teleportPoints
          .listByProject(projectId, selectedFloorPlanId)
          .catch(() => [] as TeleportPoint[]);
        setPoints(Array.isArray(pts) ? pts : []);
      } catch (err) {
        setError((err as ApiError).message || 'Failed to load teleport points');
        setPoints([]);
      }
    };

    void loadPoints();
  }, [projectId, selectedFloorPlanId, isCreatingFloor]);

  const handleStartAddFloor = () => {
    setIsCreatingFloor(true);
    setSelectedFloorPlanId(null);
    setSelectedFile(null);
    setImageUrlInput('');
    setFloorNameInput(`Floor ${floorPlans.length + 1}`);
    setPoints([]);
    setActiveMovePointId(null);
  };

  const handleDeleteFloor = async () => {
    if (!selectedFloorPlan) return;
    const name = selectedFloorPlan.name || 'Floor';
    const confirmed = window.confirm(`Delete "${name}"? Teleport points for this floor will be removed.`);
    if (!confirmed) return;
    setDeletingFloor(true);
    setError(null);
    try {
      await adminApi.floorPlans.delete(selectedFloorPlan.id);
      const nextId = await reloadFloorPlans();
      if (!nextId) {
        setIsCreatingFloor(true);
        setSelectedFloorPlanId(null);
        setPoints([]);
      }
    } catch (err) {
      setError((err as ApiError).message || 'Failed to delete floor');
    } finally {
      setDeletingFloor(false);
    }
  };

  const handleSelectFloorByIndex = (index: number) => {
    if (index < 0 || index >= floorPlans.length) return;
    setIsCreatingFloor(false);
    setSelectedFloorPlanId(floorPlans[index].id);
    setSelectedFile(null);
    setActiveMovePointId(null);
  };

  const handleSaveImageUrl = async () => {
    if (!projectId || !imageUrlInput.trim()) return;

    const floorName = floorNameInput.trim() || `Floor ${floorPlans.length + 1}`;
    setSavingImage(true);
    setError(null);

    try {
      let floor: FloorPlan;
      if (isCreatingFloor) {
        floor = await adminApi.floorPlans.createForProject(projectId, {
          name: floorName,
          imageUrl: imageUrlInput.trim(),
        });
      } else if (selectedFloorPlan) {
        floor = await adminApi.floorPlans.update(selectedFloorPlan.id, {
          name: floorName,
          imageUrl: imageUrlInput.trim(),
        });
      } else {
        throw new Error('No floor selected');
      }

      await reloadFloorPlans(floor.id);
      setIsCreatingFloor(false);
      setSelectedFile(null);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to save floor plan');
    } finally {
      setSavingImage(false);
    }
  };

  const handleUploadImage = async () => {
    if (!projectId || !selectedFile) return;

    const floorName = floorNameInput.trim() || `Floor ${floorPlans.length + 1}`;
    setSavingImage(true);
    setError(null);

    try {
      let floor: FloorPlan;
      if (isCreatingFloor) {
        floor = await adminApi.floorPlans.uploadForProject(projectId, selectedFile, floorName);
      } else if (selectedFloorPlan) {
        floor = await adminApi.floorPlans.uploadById(selectedFloorPlan.id, selectedFile, floorName);
      } else {
        throw new Error('No floor selected');
      }

      await reloadFloorPlans(floor.id);
      setIsCreatingFloor(false);
      setSelectedFile(null);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to upload floor plan');
    } finally {
      setSavingImage(false);
    }
  };

  const handleImageClick = (event: React.MouseEvent<HTMLImageElement>) => {
    if (!selectedFloorPlan || isCreatingFloor) return;

    const img = imageRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const xRatio = (event.clientX - rect.left) / rect.width;
    const yRatio = (event.clientY - rect.top) / rect.height;
    const mapX = Math.min(Math.max(xRatio, 0), 1);
    const mapY = Math.min(Math.max(yRatio, 0), 1);

    if (activeMovePointId) {
      const target = points.find((p) => p.id === activeMovePointId);
      if (!target) {
        setActiveMovePointId(null);
        return;
      }

      const updatedLocal = points.map((p) =>
        p.id === activeMovePointId ? { ...p, mapX, mapY } : p,
      );
      setPoints(updatedLocal);
      setActiveMovePointId(null);
      adminApi.teleportPoints
        .update(target.id, { mapX, mapY })
        .catch(() => setError('Failed to update point position'));
      return;
    }

    const defaultIndex = points.length + 1;
    const defaultName = `Point ${defaultIndex}`;
    adminApi.teleportPoints
      .create(selectedFloorPlan.id, {
        name: defaultName,
        mapX,
        mapY,
      })
      .then((created) => {
        setPoints((prev) => [...prev, created]);
      })
      .catch((err) => {
        setError((err as ApiError).message || 'Failed to create teleport point');
      });
  };

  const openEditPoint = (point: TeleportPoint) => {
    setEditingPoint(point);
    setPointForm({
      name: point.name,
      xPercent: (point.mapX * 100).toFixed(1),
      yPercent: (point.mapY * 100).toFixed(1),
      worldX: point.worldX != null ? String(point.worldX) : '',
      worldY: point.worldY != null ? String(point.worldY) : '',
      worldZ: point.worldZ != null ? String(point.worldZ) : '',
      worldYaw: point.worldYaw != null ? String(point.worldYaw) : '',
    });
    setPointIconFile(null);
    setPointModalOpen(true);
  };

  const handleSavePointDetails = async () => {
    if (!editingPoint) return;

    const name = pointForm.name.trim();
    if (!name) return;

    const xPercentNum =
      pointForm.xPercent.trim() !== '' ? Number.parseFloat(pointForm.xPercent) : NaN;
    const yPercentNum =
      pointForm.yPercent.trim() !== '' ? Number.parseFloat(pointForm.yPercent) : NaN;
    const worldXNum =
      pointForm.worldX.trim() !== '' ? Number.parseFloat(pointForm.worldX) : NaN;
    const worldYNum =
      pointForm.worldY.trim() !== '' ? Number.parseFloat(pointForm.worldY) : NaN;
    const worldZNum =
      pointForm.worldZ.trim() !== '' ? Number.parseFloat(pointForm.worldZ) : NaN;
    const worldYawNum =
      pointForm.worldYaw.trim() !== '' ? Number.parseFloat(pointForm.worldYaw) : NaN;

    const patch: {
      name: string;
      mapX?: number;
      mapY?: number;
      worldX?: number;
      worldY?: number;
      worldZ?: number;
      worldYaw?: number;
    } = { name };

    if (!Number.isNaN(xPercentNum)) {
      patch.mapX = Math.min(Math.max(xPercentNum / 100, 0), 1);
    }
    if (!Number.isNaN(yPercentNum)) {
      patch.mapY = Math.min(Math.max(yPercentNum / 100, 0), 1);
    }
    if (!Number.isNaN(worldXNum)) {
      patch.worldX = worldXNum;
    }
    if (!Number.isNaN(worldYNum)) {
      patch.worldY = worldYNum;
    }
    if (!Number.isNaN(worldZNum)) {
      patch.worldZ = worldZNum;
    }
    if (!Number.isNaN(worldYawNum)) {
      patch.worldYaw = worldYawNum;
    }

    setPointSaving(true);
    setError(null);
    try {
      let updated = await adminApi.teleportPoints.update(editingPoint.id, patch);
      if (pointIconFile) {
        updated = await adminApi.teleportPoints.uploadIcon(editingPoint.id, pointIconFile);
      }
      setPoints((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setPointModalOpen(false);
      setEditingPoint(null);
      setPointIconFile(null);
    } catch (err) {
      setError((err as ApiError).message || 'Failed to save teleport point (fields or icon).');
    } finally {
      setPointSaving(false);
    }
  };

  const handleDeletePoint = async (point: TeleportPoint) => {
    if (!window.confirm(`Delete teleport point "${point.name}"?`)) return;
    setError(null);
    try {
      await adminApi.teleportPoints.delete(point.id);
      setPoints((prev) => prev.filter((p) => p.id !== point.id));
    } catch (err) {
      setError((err as ApiError).message || 'Failed to delete point');
    }
  };

  const startMovePoint = (point: TeleportPoint) => {
    setActiveMovePointId(point.id);
  };

  const clearMoveMode = () => {
    setActiveMovePointId(null);
  };

  const sortedPoints = [...points].sort((a, b) => a.name.localeCompare(b.name));

  if (!projectId) {
    return (
      <Layout pageTitle="Project Floor Plan">
        <p className="text-gray-500">Project ID missing from URL.</p>
      </Layout>
    );
  }

  return (
    <Layout pageTitle="Project Floor Plan">
      <div className="space-y-4">
        <div style={{ maxWidth: '72rem', margin: '0 auto' }}>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <button
                type="button"
                className="text-sm text-primary-600 hover:text-primary-900"
                onClick={() => navigate('/projects')}
              >
                {'<-'} Back to Projects
              </button>
              <h2 className="text-xl font-semibold text-gray-900">
                {project ? project.name : 'Project'} - Floor Plans
              </h2>
              {project?.id && (
                <p className="text-xs text-gray-500">
                  Project ID:&nbsp;
                  <code className="project-id-code text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                    {project.id}
                  </code>
                </p>
              )}
            </div>
          </div>

          {error && (
            <div className="error-alert mt-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="space-y-6 mt-4">
            <div className="section-card">
              <div className="section-header">
                <h3 className="section-title">Floor Plan Images</h3>
              </div>
              <div className="section-content space-y-4">
                <div className="floorplans-toolbar">
                  <div className="floorplans-switch">
                    <Button
                      variant="outline"
                      size="sm"
                      className="floorplans-switch-btn"
                      onClick={() => handleSelectFloorByIndex(selectedFloorIndex - 1)}
                      disabled={isCreatingFloor || selectedFloorIndex <= 0}
                      aria-label="Previous floor"
                    >
                      {'<'}
                    </Button>
                    <div
                      className="floorplans-switch-label"
                      title={
                        isCreatingFloor
                          ? `New Floor (${floorPlans.length + 1})`
                          : selectedFloorPlan
                            ? `${selectedFloorPlan.name} (${selectedFloorIndex + 1}/${floorPlans.length})`
                            : 'No floors yet'
                      }
                    >
                      {isCreatingFloor
                        ? `New Floor (${floorPlans.length + 1})`
                        : selectedFloorPlan
                          ? `${selectedFloorPlan.name} (${selectedFloorIndex + 1}/${floorPlans.length})`
                          : 'No floors yet'}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="floorplans-switch-btn"
                      onClick={() => handleSelectFloorByIndex(selectedFloorIndex + 1)}
                      disabled={isCreatingFloor || selectedFloorIndex < 0 || selectedFloorIndex >= floorPlans.length - 1}
                      aria-label="Next floor"
                    >
                      {'>'}
                    </Button>
                  </div>

                  <div className="floorplans-actions">
                    <Button variant="outline" size="sm" onClick={handleStartAddFloor}>
                      + Add Floor
                    </Button>
                    {isCreatingFloor && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (floorPlans.length > 0) {
                            setIsCreatingFloor(false);
                            setSelectedFloorPlanId(floorPlans[0].id);
                          } else {
                            setIsCreatingFloor(false);
                            setSelectedFloorPlanId(null);
                          }
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    {!isCreatingFloor && selectedFloorPlan && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleDeleteFloor}
                        disabled={deletingFloor}
                      >
                        {deletingFloor ? 'Deleting...' : 'Delete Floor'}
                      </Button>
                    )}
                  </div>
                </div>

                <Input
                  label="Floor name"
                  placeholder="e.g. Ground Floor, Floor 1"
                  value={floorNameInput}
                  onChange={(e) => setFloorNameInput(e.target.value)}
                />

                <div className="space-y-2">
                  <label className="input-label">Upload floor image</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
                    className="w-full text-sm text-gray-700"
                  />
                  <div className="flex items-center gap-3 mt-2">
                    <Button onClick={handleUploadImage} disabled={savingImage || !selectedFile}>
                      {savingImage
                        ? 'Uploading...'
                        : isCreatingFloor
                          ? 'Create Floor via Upload'
                          : 'Replace Image'}
                    </Button>
                    <p className="text-xs text-gray-500">
                      Upload from your computer for this floor.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Input
                    label="Or paste image URL"
                    placeholder="https://example.com/floorplan.png"
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                  />
                  <div className="flex items-center gap-3">
                    <Button onClick={handleSaveImageUrl} disabled={savingImage || !imageUrlInput.trim()}>
                      {savingImage
                        ? 'Saving...'
                        : isCreatingFloor
                          ? 'Create Floor via URL'
                          : 'Update via URL'}
                    </Button>
                    <p className="text-xs text-gray-500">Use direct image URL for current floor.</p>
                  </div>
                </div>

                {loading ? (
                  <p className="text-gray-500 mt-4">Loading...</p>
                ) : isCreatingFloor || !selectedFloorPlan ? (
                  <p className="text-gray-500 mt-4">
                    {floorPlans.length === 0
                      ? 'No floor configured yet. Click + Add Floor and save an image.'
                      : 'You are creating a new floor. Save image first, then add teleport points.'}
                  </p>
                ) : (
                  <div className="mt-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Click on the image to add a teleport point.
                      {activeMovePointId && (
                        <span className="ml-2 text-primary-600 font-medium">
                          Click to place the selected point at new position.
                        </span>
                      )}
                    </p>
                    <div
                      className="floorplan-container relative inline-block w-full max-w-full bg-gray-900 rounded-lg overflow-hidden border border-gray-700 cursor-pointer"
                      style={{ zIndex: 0 }}
                    >
                      <img
                        ref={imageRef}
                        src={resolveBackendAssetUrl(selectedFloorPlan.imageUrl)}
                        alt={selectedFloorPlan.name}
                        onClick={handleImageClick}
                        style={{ width: '100%', height: 'auto', display: 'block' }}
                      />
                      {points.map((point) =>
                        point.iconUrl ? (
                          <button
                            key={point.id}
                            type="button"
                            style={{
                              position: 'absolute',
                              left: `${point.mapX * 100}%`,
                              top: `${point.mapY * 100}%`,
                              transform: 'translate(-50%, -50%)',
                              border: 'none',
                              background: 'transparent',
                              padding: 0,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditPoint(point);
                            }}
                            title={point.name}
                          >
                            <img
                              src={resolveBackendAssetUrl(point.iconUrl)}
                              alt={point.name}
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '9999px',
                                border: '2px solid white',
                                boxShadow: '0 0 6px rgba(0,0,0,0.6)',
                              }}
                            />
                          </button>
                        ) : (
                          <button
                            key={point.id}
                            type="button"
                            className={`absolute rounded-full floorplan-point ${
                              activeMovePointId === point.id ? 'floorplan-point--active' : ''
                            } text-xs font-semibold shadow-sm`}
                            style={{
                              position: 'absolute',
                              left: `${point.mapX * 100}%`,
                              top: `${point.mapY * 100}%`,
                              transform: 'translate(-50%, -50%)',
                              padding: '0.25rem 0.4rem',
                              minWidth: '1.5rem',
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditPoint(point);
                            }}
                            title={point.name}
                          >
                            {point.name.charAt(0).toUpperCase()}
                          </button>
                        )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="section-card relative" style={{ zIndex: 1 }}>
              <div className="section-header">
                <h3 className="section-title">
                  Teleport Points {selectedFloorPlan ? `(${selectedFloorPlan.name})` : ''}
                </h3>
              </div>
              <div className="section-content">
                {points.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    {selectedFloorPlan
                      ? 'No teleport points yet. Click on the floor image to add one.'
                      : 'Select or create a floor to manage teleport points.'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {sortedPoints.map((point) => (
                      <div
                        key={point.id}
                        className={`p-3 rounded-md border ${
                          activeMovePointId === point.id
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-gray-200 bg-white'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">{point.name}</p>
                            <p className="text-xs text-gray-500">
                              x={(point.mapX * 100).toFixed(1)}%, y={(point.mapY * 100).toFixed(1)}%
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEditPoint(point)}>
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              activeMovePointId === point.id ? clearMoveMode() : startMovePoint(point)
                            }
                          >
                            {activeMovePointId === point.id ? 'Cancel Move' : 'Move'}
                          </Button>
                          <Button variant="danger" size="sm" onClick={() => handleDeletePoint(point)}>
                            Delete
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={pointModalOpen}
        onClose={() => {
          setPointModalOpen(false);
          setEditingPoint(null);
          setPointIconFile(null);
          setPointForm(emptyPointForm);
        }}
        title={editingPoint ? 'Edit Teleport Point' : 'Teleport Point'}
        footer={
          <>
            <Button
              variant="outline"
              onClick={() => {
                setPointModalOpen(false);
                setEditingPoint(null);
                setPointIconFile(null);
                setPointForm(emptyPointForm);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSavePointDetails} disabled={pointSaving || !pointForm.name.trim()}>
              {pointSaving ? 'Saving...' : 'Save'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Input
            label="Point name"
            placeholder="e.g., Bedroom door"
            value={pointForm.name}
            onChange={(e) => setPointForm((prev) => ({ ...prev, name: e.target.value }))}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="X on image (%)"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder="e.g. 25"
              value={pointForm.xPercent}
              onChange={(e) => setPointForm((prev) => ({ ...prev, xPercent: e.target.value }))}
            />
            <Input
              label="Y on image (%)"
              type="number"
              step="0.1"
              min="0"
              max="100"
              placeholder="e.g. 40"
              value={pointForm.yPercent}
              onChange={(e) => setPointForm((prev) => ({ ...prev, yPercent: e.target.value }))}
            />
          </div>
          <div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Input
                label="X Coordinate"
                type="number"
                step="0.1"
                placeholder="0.0"
                value={pointForm.worldX}
                onChange={(e) => setPointForm((prev) => ({ ...prev, worldX: e.target.value }))}
              />
              <Input
                label="Y Coordinate"
                type="number"
                step="0.1"
                placeholder="0.0"
                value={pointForm.worldY}
                onChange={(e) => setPointForm((prev) => ({ ...prev, worldY: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <Input
                label="Z Coordinate"
                type="number"
                step="0.1"
                placeholder="0.0"
                value={pointForm.worldZ}
                onChange={(e) => setPointForm((prev) => ({ ...prev, worldZ: e.target.value }))}
              />
              <Input
                label="Rotation (Yaw) - Optional"
                type="number"
                step="1"
                placeholder="0"
                value={pointForm.worldYaw}
                onChange={(e) => setPointForm((prev) => ({ ...prev, worldYaw: e.target.value }))}
              />
            </div>
            <label className="input-label mt-4">Point icon (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setPointIconFile(e.target.files?.[0] ?? null)}
              className="w-full text-sm text-gray-700"
            />
            {editingPoint?.iconUrl && !pointIconFile && (
              <div className="mt-2 text-xs text-gray-500">
                Current icon:
                <img
                  src={resolveBackendAssetUrl(editingPoint.iconUrl)}
                  alt={editingPoint.name}
                  style={{ display: 'inline-block', width: 24, height: 24, marginLeft: 8 }}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>
    </Layout>
  );
};

export default ProjectFloorPlan;
